import "@/App.css";
import { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { LanguageProvider, useLang } from "@/context/LanguageContext";
import { urlLangPrefix } from "@/lib/i18n";
import ErrorBoundary, { NotSchranke } from "@/components/ErrorBoundary";
import { ThemeProvider } from "@/context/ThemeContext";
import { FavoritesProvider } from "@/context/FavoritesContext";
import { UnreadProvider } from "@/context/UnreadContext";
import Layout from "@/components/Layout";
import ScrollToTop from "@/components/ScrollToTop";
import Vorhang from "@/components/Vorhang";

// ---------------------------------------------------------------------------
// Route-level code splitting.
// Every page becomes its own webpack chunk that is only downloaded when the
// route is first visited. This keeps the initial JS bundle small — the heavy
// pages (Dashboard ~49KB, CreateListing ~44KB, PropertyDetail ~39KB, plus
// leaflet / recharts that they pull in) no longer load on the
// landing page.
// ---------------------------------------------------------------------------
const Home = lazy(() => import("@/pages/Home"));
const Search = lazy(() => import("@/pages/Search"));
// Vollbild-Karte für den WebView der nativen App (ohne globales Chrome, s. Layout.jsx).
const MapEmbed = lazy(() => import("@/pages/MapEmbed"));
const PropertyDetail = lazy(() => import("@/pages/PropertyDetail"));
const ProjectDetail = lazy(() => import("@/pages/ProjectDetail"));
const Login = lazy(() => import("@/pages/Login"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const VerifyEmail = lazy(() => import("@/pages/VerifyEmail"));
const Register = lazy(() => import("@/pages/Register"));
const Financing = lazy(() => import("@/pages/Financing"));
const Wertermittlung = lazy(() => import("@/pages/Wertermittlung"));
const WertermittlungWizard = lazy(() => import("@/pages/WertermittlungWizard"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const AdminNutzerakte = lazy(() => import("@/pages/AdminNutzerakte"));
const CreateListing = lazy(() => import("@/pages/CreateListing"));
const CreateProject = lazy(() => import("@/pages/CreateProject"));
const Pakete = lazy(() => import("@/pages/Pakete"));
const MaklerPakete = lazy(() => import("@/pages/MaklerPakete"));
const DeveloperPakete = lazy(() => import("@/pages/DeveloperPakete"));
const Messages = lazy(() => import("@/pages/Messages"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));
const AlertAction = lazy(() => import("@/pages/AlertAction"));
const Anbieter = lazy(() => import("@/pages/Anbieter"));
const NotFound = lazy(() => import("@/pages/NotFound"));

// Pages that use *named* exports need a tiny wrapper so lazy() gets a `default`.
const About = lazy(() => import("@/pages/StaticPages").then((m) => ({ default: m.About })));
const Contact = lazy(() => import("@/pages/StaticPages").then((m) => ({ default: m.Contact })));
const Impressum = lazy(() => import("@/pages/StaticPages").then((m) => ({ default: m.Impressum })));
const Datenschutz = lazy(() => import("@/pages/StaticPages").then((m) => ({ default: m.Datenschutz })));
const CookieRichtlinie = lazy(() => import("@/pages/StaticPages").then((m) => ({ default: m.CookieRichtlinie })));
const AGB = lazy(() => import("@/pages/StaticPages").then((m) => ({ default: m.AGB })));
const BlogList = lazy(() => import("@/pages/Blog").then((m) => ({ default: m.BlogList })));
const BlogDetail = lazy(() => import("@/pages/Blog").then((m) => ({ default: m.BlogDetail })));
// Ratgeber = kuratierte, versionierte Leitfäden (statisch). Der DB-gestützte Blog
// (BlogList/BlogDetail, admin-editierbar) lebt jetzt unter /aktuelles.
const RegionPage = lazy(() => import("@/pages/RegionPage"));
const RatgeberList = lazy(() => import("@/pages/Ratgeber").then((m) => ({ default: m.RatgeberList })));
const RatgeberDetail = lazy(() => import("@/pages/Ratgeber").then((m) => ({ default: m.RatgeberDetail })));

function PageLoader() {
  // Rendert innerhalb des LanguageProviders (Suspense-Fallback im Layout).
  const { t } = useLang();
  // min-h-screen statt der frueheren 60vh, und das ist kein Schoenheitsgrund:
  // Bei 60vh stand der Footer waehrend des Nachladens MITTEN IM BILD und sprang
  // weg, sobald der Routen-Chunk ankam. Gemessen war das der mit Abstand
  // groesste Layout-Shift der Startseite — 0,168 von 0,170 CLS, aus einem
  // einzigen Sprung. Volle Viewport-Hoehe schiebt den Footer von vornherein
  // unter die Falz; verschiebt er sich dann noch, sieht es niemand und es
  // zaehlt nicht. Details: memory/SEO_AUDIT_2026-08-04.md, Befund 4.1.
  return (
    <div className="min-h-screen grid place-items-center" data-testid="route-loader">
      <div className="flex flex-col items-center gap-4 text-anthracite/70">
        <div className="w-8 h-8 rounded-full border-2 border-anthracite/15 border-t-terracotta animate-spin" />
        <span className="text-xs font-semibold uppercase tracking-[0.12em]">{t("common.loading")}</span>
      </div>
    </div>
  );
}

function App() {
  return (
    <div className="App">
      {/* ThemeProvider ganz außen (hängt weder an Auth noch an Sprache);
          AuthProvider vor LanguageProvider: der liest die Profilsprache via useAuth(). */}
      {/* Aeussere Schranke GANZ aussen: Wirft ein Provider selbst (Auth, Sprache,
          Theme), gaebe es weder t() noch Layout — die innere Auffangseite wuerde
          mit abstuerzen. Diese hier kommt ohne beides aus. */}
      <NotSchranke>
      <ThemeProvider>
      {/* Vorhang vor allem, was die API anspricht — solange Avliana in Gründung
          ist, kommt nur durch, wer das Passwort kennt (components/Vorhang.jsx). */}
      <Vorhang>
      <AuthProvider>
        {/* FavoritesProvider hinter AuthProvider: er lädt die Merkliste, sobald
            eine Anmeldung feststeht, und leert sie beim Abmelden. */}
        <FavoritesProvider>
        {/* UnreadProvider hinter AuthProvider: pollt den Ungelesen-Zähler für
            die Navigations-Badges, sobald eine Anmeldung feststeht. */}
        <UnreadProvider>
        <LanguageProvider>
          {/* basename = Sprach-Präfix der URL (/de, /el, … — Englisch läuft ohne
              Präfix). Damit funktionieren ALLE bestehenden <Link to="/suche">
              und navigate("/…")-Aufrufe unverändert unter jedem Präfix: Der
              Router hängt ihn selbst an. Ein basename ist zur Laufzeit fix —
              der Sprachwechsel über die Umschalter navigiert deshalb voll
              (switchLanguage im LanguageProvider). Ein UNBEKANNTES Präfix
              (/fr/...) ergibt basename "" → der Catch-All rendert die
              404-Seite mit noindex, genau richtig. */}
          <BrowserRouter basename={urlLangPrefix() ? `/${urlLangPrefix()}` : undefined}>
            <ScrollToTop />
            <Layout>
              {/* Fehlerschranke um die Routen: Stuerzt EINE Seite ab, bleiben
                  Kopf- und Fusszeile stehen und der Nutzer kann weiterklicken.
                  Innerhalb des LanguageProviders, damit die Auffangseite
                  uebersetzt ist. */}
              <ErrorBoundary>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/suche" element={<Search />} />
                  {/* Kaufen und Mieten sind IMMER getrennte Listen — /kaufen rendert
                      nicht mehr die ungefilterte Suche (dort mischten sich Mietinserate
                      unter die Kaufangebote). */}
                  <Route path="/kaufen" element={<Navigate to="/suche?listing_type=buy" replace />} />
                  <Route path="/karte-embed" element={<MapEmbed />} />
                  <Route path="/mieten" element={<Navigate to="/suche?listing_type=rent" replace />} />
                  {/* „Neubauprojekte" ist eine ANGEBOTSART, kein Immobilientyp.
                      Bis zum 11.08.2026 stand hier `property_type=new_build` —
                      und beides heißt `new_build`, deshalb fiel es niemandem
                      auf: Der Menüpunkt lud die Suche, die Filterspalte zeigte
                      „Angebotsart: Alle Angebote" und darunter „Immobilientyp:
                      Neubau". Also ein Filter, den der Nutzer nicht gewählt
                      hatte, statt des einen, den der Menüpunkt verspricht —
                      und Projekte, die nach ihrer Angebotsart gesucht werden,
                      fehlten in der Liste.
                      `offer_source=development` ist der Wert hinter
                      `proj.os_development` („Neubauprojekte") in der
                      Filterspalte; ProjectDetail verlinkt seit jeher dorthin. */}
                  <Route path="/neubau" element={<Navigate to="/suche?offer_source=development" replace />} />
                  <Route path="/gewerbe" element={<Navigate to="/suche?property_type=commercial" replace />} />
                  <Route path="/immobilie/:id" element={<PropertyDetail />} />
                  <Route path="/projekt/:slug" element={<ProjectDetail />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/passwort-vergessen" element={<ForgotPassword />} />
                  <Route path="/passwort-zuruecksetzen" element={<ResetPassword />} />
                  <Route path="/e-mail-bestaetigen" element={<VerifyEmail />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/finanzierung" element={<Financing />} />
                  <Route path="/wertermittlung" element={<Wertermittlung />} />
                  <Route path="/wertermittlung/objekt" element={<WertermittlungWizard />} />
                  <Route path="/inserat-erstellen" element={<CreateListing />} />
                  <Route path="/projekt-erstellen" element={<CreateProject />} />
                  <Route path="/mein-bereich/inserate/:id/bearbeiten" element={<CreateListing />} />
                  <Route path="/pakete" element={<Pakete />} />
                  <Route path="/inserieren" element={<Pakete />} />
                  <Route path="/makler-pakete" element={<MaklerPakete />} />
                  <Route path="/developer-pakete" element={<DeveloperPakete />} />
                  <Route path="/nachrichten" element={<Messages />} />
                  <Route path="/nachrichten/:conversationId" element={<Messages />} />
                  <Route path="/auth-callback" element={<AuthCallback />} />
                  <Route path="/suchauftrag/bestaetigen" element={<AlertAction mode="confirm" />} />
                  <Route path="/suchauftrag/abmelden" element={<AlertAction mode="unsubscribe" />} />
                  <Route path="/anbieter/:id" element={<Anbieter />} />
                  {/* Objektvergleich entfernt (30.09.2026). Die Route bleibt als
                      Weiterleitung, weil bereits installierte App-Versionen und
                      verschickte Links weiter /vergleich?ids=… öffnen. */}
                  <Route path="/vergleich" element={<Navigate to="/mein-bereich?tab=favorites" replace />} />
                  <Route path="/mein-bereich" element={<Dashboard />} />
                  <Route path="/makler-dashboard" element={<Dashboard />} />
                  <Route path="/developer-dashboard" element={<Dashboard />} />
                  <Route path="/admin" element={<AdminDashboard />} />
                  <Route path="/admin/nutzer/:id" element={<AdminNutzerakte />} />
                  <Route path="/region/:slug" element={<RegionPage />} />
                  <Route path="/ratgeber" element={<RatgeberList />} />
                  <Route path="/ratgeber/:slug" element={<RatgeberDetail />} />
                  <Route path="/aktuelles" element={<BlogList />} />
                  <Route path="/aktuelles/:slug" element={<BlogDetail />} />
                  <Route path="/ueber-uns" element={<About />} />
                  <Route path="/kontakt" element={<Contact />} />
                  <Route path="/impressum" element={<Impressum />} />
                  <Route path="/datenschutz" element={<Datenschutz />} />
                  <Route path="/cookie-richtlinie" element={<CookieRichtlinie />} />
                  <Route path="/agb" element={<AGB />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
              </ErrorBoundary>
            </Layout>
          </BrowserRouter>
        </LanguageProvider>
        </UnreadProvider>
        </FavoritesProvider>
      </AuthProvider>
      </Vorhang>
      </ThemeProvider>
      </NotSchranke>
    </div>
  );
}

export default App;
